import { AusRequirement, TerminalAction } from 'app/pages/programs/channels/Channels.constants';
import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';

import { LoanEditorActionsEnum } from 'constants/loanActionEditor';

export const STEP_KEY = {
  REGISTRATION: 'REGISTRATION',
  PRICING: 'PRICING',
  LOCK: 'LOCK',
  AUS: 'AUS',
  DOCUMENTS_CONDITIONS: 'DOCUMENTS_CONDITIONS',
  DISCLOSURES: 'DISCLOSURES',
};

export const STEP_TITLE = {
  [STEP_KEY.REGISTRATION]: 'Registration complete',
  [STEP_KEY.PRICING]: 'Pricing',
  [STEP_KEY.LOCK]: 'Rate lock',
  [STEP_KEY.AUS]: 'AUS / DU',
  [STEP_KEY.DOCUMENTS_CONDITIONS]: 'Conditions cleared',
  [STEP_KEY.DISCLOSURES]: 'Disclosures',
};

export const STEP_SUBTITLE = {
  [STEP_KEY.REGISTRATION]: 'MISMO file imported and loan created',
  [STEP_KEY.PRICING]: 'Select a product and price the loan',
  [STEP_KEY.LOCK]: 'Lock the interest rate',
  [STEP_KEY.AUS]: 'Run automated underwriting system',
  [STEP_KEY.DOCUMENTS_CONDITIONS]: 'Resolve outstanding conditions',
  [STEP_KEY.DISCLOSURES]: 'Send initial loan disclosures to borrower',
};

// Short labels rendered under each node in the horizontal status rail.
export const STEP_SHORT_LABEL = {
  [STEP_KEY.REGISTRATION]: 'Registration',
  [STEP_KEY.PRICING]: 'Pricing',
  [STEP_KEY.LOCK]: 'Rate lock',
  [STEP_KEY.AUS]: 'AUS / DU',
  [STEP_KEY.DOCUMENTS_CONDITIONS]: 'Conditions',
  [STEP_KEY.DISCLOSURES]: 'Disclosures',
};

export const STEP_NODE_STATUS = {
  DONE: 'DONE',
  BLOCKING: 'BLOCKING',
  CURRENT: 'CURRENT',
  TODO: 'TODO',
};

export const STEP_NODE_STATUS_LABEL = {
  [STEP_NODE_STATUS.DONE]: 'Done',
  [STEP_NODE_STATUS.BLOCKING]: 'Action needed',
  [STEP_NODE_STATUS.CURRENT]: 'In progress',
  [STEP_NODE_STATUS.TODO]: 'Upcoming',
};

export const NEXT_ACTION_SEVERITY = {
  BLOCKING: 'BLOCKING',
  IN_PROGRESS: 'IN_PROGRESS',
  READY: 'READY',
};

// Copy for the single next-action callout, keyed by the active step.
export const NEXT_ACTION_COPY = {
  [STEP_KEY.PRICING]: {
    title: 'Next: Select pricing',
    description: 'Choose a product and price the loan to continue toward submission.',
  },
  [STEP_KEY.LOCK]: {
    title: 'Next: Lock the rate',
    description: 'Lock the interest rate for this loan.',
  },
  [STEP_KEY.AUS]: {
    title: 'Next: Run AUS / DU findings',
    description:
      'Required before this loan can be submitted. No findings on the current profile yet.',
  },
  [STEP_KEY.DOCUMENTS_CONDITIONS]: {
    title: 'Next: Clear outstanding conditions',
    description: 'Resolve the outstanding conditions on this loan before submission.',
  },
  [STEP_KEY.DISCLOSURES]: {
    title: 'Next: Send disclosures',
    description: 'Hand off to the disclosure flow to send initial loan disclosures.',
  },
};

export const READY_ACTION_COPY = {
  title: 'Ready to submit',
  description: 'All required steps for this channel are complete.',
};

export const TERMINAL_ACTION_BUTTON_LABEL = {
  [TerminalAction.SUBMIT_TO_UNDERWRITING]: 'Submit to Underwriting',
  [TerminalAction.DELIVER_TO_INVESTOR]: 'Deliver to Investor',
  [TerminalAction.PURCHASE_CLEARED]: 'Purchase Cleared',
};

// Maps the channel's terminal action to the loan-action that performs it (when available).
export const TERMINAL_ACTION_LOAN_ACTION = {
  [TerminalAction.SUBMIT_TO_UNDERWRITING]: LoanEditorActionsEnum.SUBMIT_APPLICATION,
};

export const AUS_STEP_LABEL = {
  [AusRequirement.OPTIONAL]: 'Optional',
  [AusRequirement.REQUIRED]: 'Required',
};

// Wholesale (sub-company) loans retrieve findings via the broker's existing casefile
// instead of running a fresh AUS submission.
export const WHOLESALE_AUS_BADGE = { label: 'Wholesale', variant: LabelVariant.BLUE };

export const WHOLESALE_AUS_SUBTITLE =
  'Retrieve findings via the broker’s existing casefile — no new casefile is created';

export const WHOLESALE_AUS_CTA_LABEL = 'Enter Casefile IDs';

export const WHOLESALE_AUS_NEXT_ACTION = {
  title: 'Next: Provide AUS casefile IDs',
  description:
    'Enter the broker’s DU Casefile ID and/or LPA AUS Key to retrieve findings under your credentials.',
};

// Shown while a wholesale AUS retrieval is running (severity IN_PROGRESS).
export const WHOLESALE_AUS_IN_PROGRESS_TITLE = 'Retrieving AUS findings…';

export const WHOLESALE_AUS_LEG_SKIPPED_TEXT = 'no key provided, leg skipped';

// Shown when the latest wholesale AUS operation ended in error (severity BLOCKING).
export const WHOLESALE_AUS_ERROR_TITLE = 'AUS retrieval failed';

export const WHOLESALE_AUS_ERROR_FALLBACK_DESCRIPTION =
  'AUS retrieval failed. Review the casefile IDs and try again.';

export const WHOLESALE_AUS_EDIT_CTA_LABEL = 'Edit Casefile IDs';

export const AUS_LEG = {
  DU: 'DU',
  LP: 'LPA',
};

export const AUS_LEG_ID_PREFIX = {
  [AUS_LEG.DU]: 'Casefile',
  [AUS_LEG.LP]: 'Key',
};

// The backend serializes the aggregated per-leg error as "Du: <msg> | Lp: <msg>".
export const AUS_LEG_ERROR_PREFIX = {
  [AUS_LEG.DU]: 'du',
  [AUS_LEG.LP]: 'lp',
};

export const AUS_RETRY_LABEL = {
  [AUS_LEG.DU]: 'Retry DU Retrieval',
  [AUS_LEG.LP]: 'Retry LPA Retrieval',
  BOTH: 'Retry Retrieval',
};

export const AUS_LEG_CHIP_VARIANT = {
  SUCCESS: LabelVariant.GREEN,
  RETRIEVING: LabelVariant.BLUE,
  ERROR: LabelVariant.RED,
  NEUTRAL: LabelVariant.GRAY,
};
